import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Separator } from '@radix-ui/react-select';
import * as Typography from '@/components/shadcn/typography';
import { ContentSection, normalizeForAnchor } from '@/constants/tos';
import CopyToClipboard from '@/components/ui/shared/CopyToClipboard';

interface Props {
  section: ContentSection;
  setCurrentContent: Dispatch<SetStateAction<string | null>>;
  root: React.RefObject<HTMLDivElement>;
}

export default function Section({ section, setCurrentContent, root }: Props) {
  const baseUrl =
    typeof window !== 'undefined'
      ? `${location?.origin}${location?.pathname}`
      : '';

  const { ref, entry, inView } = useInView({
    threshold: 0,
    root: root.current,
    rootMargin: '-25% 0px -75% 0px',
  });

  useEffect(() => {
    if (inView) {
      setCurrentContent(section.title);
    }
  }, [inView, entry, setCurrentContent, section.title]);

  return (
    <div ref={ref} className="relative flex flex-col" key={section.title}>
      <div
        id={normalizeForAnchor(section.title)}
        className="absolute top-[-200px]"
      />
      <div className="mb-3 flex items-center gap-2">
        <Typography.H5>{section.title}</Typography.H5>
        <CopyToClipboard
          content={`${baseUrl}#${normalizeForAnchor(section.title)}`}
        />
      </div>
      <div className="flex flex-col gap-4">
        {section.list && <Typography.List list={section.list} />}
        {section.content?.map((content, index) => (
          <p
            key={index.toString()}
            dangerouslySetInnerHTML={{ __html: content }}
            className="[&>a]:text-azure-500"
          />
        ))}
      </div>
      {section.nestedItems?.map(nestedItem => (
        <div className="py-3" key={nestedItem.title}>
          <Typography.H6>{nestedItem.title}</Typography.H6>
          {nestedItem.description && (
            <Typography.P>{nestedItem.description}</Typography.P>
          )}
          {nestedItem.list && <Typography.List list={nestedItem.list} />}
          {nestedItem.content?.map((content, index) => (
            <p
              key={index.toString()}
              dangerouslySetInnerHTML={{ __html: content }}
              className="[&>a]:text-azure-500"
            />
          ))}
        </div>
      ))}
      <Separator className="my-7" />
    </div>
  );
}
